import React from "react";
import { Icon } from "@iconify-icon/react";
import Select from "react-select";
import DialogLayout from "../../../layout/DialogLayout";

import styles from "./ManageStoresModal.module.scss";
import {
  claimStoreService,
  getBorrowedStoresService,
  getLentStoresService,
  getStoreInfoService,
  returnStoreService,
  transferStoreService,
} from "../../../services/clients";
import {
  AlertType,
  usePopupAlert,
} from "../../../providers/AlertProvider/AlertProvider";
import { useParams } from "react-router-dom";
import {
  BusinessClient,
  ClientsFilter,
  FilterTypesClients,
  StoreData,
} from "../../../utils/typesClients.d";
import { Button } from "../../../components";
import SelectButton from "../../../components/Checkbox/Checkbox";
import { useClientsContext } from "../../../providers/clientsProvider";
import CustomSelect, {
  optionType,
} from "../../../components/CustomSelect/CustomSelect";
import { useTranslation } from "react-i18next";

export interface ManageStoreModalProps {
  open: boolean;
  onClose: (reload?: boolean) => void;
}

enum ManageStoreOptions {
  transferStores = "transferStores",
  recoverStores = "recoverStores",
  returnStores = "returnStores",
}

interface SimpleStoreData {
  id: string;
  name: string;
}

export default function ManageStoresModal({
  open,
  onClose,
}: ManageStoreModalProps) {
  const { t } = useTranslation();
  const { id } = useParams();
  const showAlert = usePopupAlert();
  const [title, setTitle] = React.useState<string>(t('title.manage_stores').toString());
  const [activeOption, setActiveOption] = React.useState<
    ManageStoreOptions | ""
  >("");
  const [stores, setStores] = React.useState<SimpleStoreData[]>([]);
  const [selectedStores, setSelectedStores] = React.useState<string[]>([]);
  const [noMoreStores, setNoMoreStores] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [clientSelected, setClientSelected] = React.useState<string>("");

  const [pagination, setPagination] = React.useState<{
    page: number;
    size: number;
  }>({ page: 0, size: 50 });

  const { clients, fetchClients } = useClientsContext();

  const handleOptionSelect = (option: ManageStoreOptions) => {
    setActiveOption(option);
    getStoresData(option);
    switch (option) {
      case ManageStoreOptions.transferStores:
        setTitle(t('title.transfer_stores').toString());
        break;
      case ManageStoreOptions.recoverStores:
        setTitle(t('title.recover_stores').toString());
        break;
      case ManageStoreOptions.returnStores:
        setTitle(t('title.return_stores').toString());
        break;
      default:
        setTitle(t('title.manage_stores').toString());
    }
  };

  const parseStoreData = (
    data: StoreData[] | FiraStore[]
  ): SimpleStoreData[] => {
    return data.map((store) => {
      if ("storeId" in store) {
        return {
          id: store.storeId,
          name: store.storeName,
        };
      } else {
        return {
          id: store.id,
          name: store.name,
        };
      }
    });
  };

  const parseClientData = (data: BusinessClient[]) => {
    return data
      .map((client) => ({
        label: client.fullName,
        value: client.clientId,
      }))
      .filter((client) => client.value !== id);
  };

  const getStoresData = async (option: ManageStoreOptions) => {
    try {
      setLoading(true);
      switch (option) {
        case ManageStoreOptions.transferStores: {
          // fetch stores to transfer
          fetchClients(
            FilterTypesClients.default,
            "none",
            ClientsFilter.ascend
          );
          const storesToTransfer = (
            await getStoreInfoService(
              id ? id : "",
              ClientsFilter.noMainStore,
              pagination.page,
              pagination.size,
              ""
            )
          ).data;
          if (storesToTransfer.length < pagination.size) {
            setNoMoreStores(true);
          }
          setStores((prev) => [...prev, ...parseStoreData(storesToTransfer)]);
          break;
        }
        case ManageStoreOptions.recoverStores: {
          // fetch stores to recover
          const lentStores = await getLentStoresService(
            id ? id : "",
            pagination.page,
            pagination.size
          );
          if (lentStores.length < pagination.size) {
            setNoMoreStores(true);
          }
          setStores((prev) => [...prev, ...parseStoreData(lentStores)]);
          break;
        }
        case ManageStoreOptions.returnStores: {
          // fetch stores to return
          const borrowedStores = await getBorrowedStoresService(
            id ? id : "",
            pagination.page,
            pagination.size
          );
          if (borrowedStores.length < pagination.size) {
            setNoMoreStores(true);
          }
          setStores((prev) => [...prev, ...parseStoreData(borrowedStores)]);
          break;
        }
        default:
          setNoMoreStores(true);
          setStores([]);
          break;
      }
      setPagination((prev) => ({ ...prev, page: prev.page + 1 }));
    } catch (error) {
      showAlert(AlertType.error, "Error fetching stores data.");
      setStores([]);
    } finally {
      setLoading(false);
    }
  };

  const onStoreSelect = (storeId: string) =>
    setSelectedStores((prev) => {
      if (prev.includes(storeId)) {
        return prev.filter((id) => id !== storeId);
      } else {
        return [...prev, storeId];
      }
    });

  const onClientSelect = (clientId: string) => {
    setClientSelected(clientId);
  };

  const simpleListAction = async () => {
    setLoading(true);
    try {
      switch (activeOption) {
        case ManageStoreOptions.returnStores:
          await returnStoreService(id ? id : "", selectedStores);
          break;
        case ManageStoreOptions.recoverStores:
          await claimStoreService(id ? id : "", selectedStores);
          break;
        case ManageStoreOptions.transferStores:
          await transferStoreService(
            clientSelected,
            selectedStores
          );
          break;
        default:
          break;
      }
      showAlert(
        AlertType.success,
        t(`alert.${activeOption}`)
      );
      onClose(true);
    } catch (error) {
      showAlert(AlertType.error, t('alert.errorAction'));
    } finally {
      setLoading(false);
    }
  };

  const renderTranferStoreOption = (option: ManageStoreOptions) => (
    <div className={styles.optionContainer}>
      <p className={styles.instructionText}>
        {t(`instructions.${option}`)}
      </p>
      <div className={styles.storeListWrapper}>
        {stores.map((store) => (
          <div
            onClick={() => onStoreSelect(store.id)}
            className={styles.listItem}
            key={store.id}
          >
            <SelectButton
              isActive={selectedStores.includes(store.id)}
              inputName={store.id}
              clickHandler={() => onStoreSelect(store.id)}
            />
            <p>{store.name}</p>
          </div>
        ))}
      </div>
      <div className={styles.loadMoreButton}>
        {!noMoreStores && (
          <Button clickHandler={() => getStoresData(option)}>
            <p>{t('button.load_more_stores')}</p>
          </Button>
        )}
      </div>
      
      <p className={styles.instructionText}>
        {t('instructions.selectClient')}
      </p>
      <div className={styles.transferSelectContainer}>
        <Select
          options={parseClientData(clients)}
          onChange={(e) => e && onClientSelect(e.value)}
        />
        <Button
          clickHandler={simpleListAction}
          isDisabled={selectedStores.length === 0 || clientSelected === ""}
        >
          <p>{t(`button.${option}`)}</p>
        </Button>
      </div>
    </div>
  );

  const renderStoreSimpleStoreList = (stores: SimpleStoreData[]) => {
    return (
      <div className={styles.storeListContainer}>
        <div className={styles.storeListWrapper}>
          {stores.map((store) => (
            <div
              onClick={() => onStoreSelect(store.id)}
              className={styles.listItem}
              key={store.id}
            >
              <SelectButton
                isActive={selectedStores.includes(store.id)}
                inputName={store.id}
                clickHandler={() => onStoreSelect(store.id)}
              />
              <p>{store.name}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSimpleStoreListAction = (option: ManageStoreOptions) => {
    return (
      <div>
        <p className={styles.instructionText}>
          {t(`instructions.${option}`)}
        </p>
        {renderStoreSimpleStoreList(stores)}
        {!noMoreStores && (
          <Button clickHandler={() => getStoresData(option)}>
            <p>{t('button.load_more_stores')}</p>
          </Button>
        )}
        <Button
          clickHandler={simpleListAction}
          isDisabled={selectedStores.length === 0}
        >
          <p>
            {t(`button.${activeOption}`)}
          </p>
        </Button>
      </div>
    );
  };

  React.useEffect(() => {
    if (!open) {
      setActiveOption("");
      setTitle(t('title.manage_stores').toString());
      setStores([]);
      setSelectedStores([]);
      setNoMoreStores(false);
      setPagination(prev => ({ ...prev, page: 0 }));
      setClientSelected("");
    }
  }, [open]);

  return (
    <DialogLayout open={open} onClose={onClose} title={title}>
      <div>
        {activeOption === "" && (
          <div className={styles.optionsWrapper}>
            <button
              type="button"
              className={styles.optionButton}
              onClick={() =>
                handleOptionSelect(ManageStoreOptions.transferStores)
              }
            >
              <Icon icon="streamline:transfer-van" style={{ fontSize: 40 }} />
              <span>{t('title.transfer_stores')}</span>
            </button>
            <button
              type="button"
              className={styles.optionButton}
              onClick={() =>
                handleOptionSelect(ManageStoreOptions.recoverStores)
              }
            >
              <Icon
                icon="pepicons-pencil:rewind-time"
                style={{ fontSize: 40 }}
              />
              <span>{t('title.recover_stores')}</span>
            </button>
            <button
              type="button"
              className={styles.optionButton}
              onClick={() =>
                handleOptionSelect(ManageStoreOptions.returnStores)
              }
            >
              <Icon icon="streamline:return-2" style={{ fontSize: 40 }} />
              <span>{t('title.return_stores')}</span>
            </button>
          </div>
        )}
        {activeOption !== "" && !loading && (
          <>
            {activeOption !== ManageStoreOptions.transferStores
              ? renderSimpleStoreListAction(activeOption)
              : renderTranferStoreOption(activeOption)}
          </>
        )}
      </div>
    </DialogLayout>
  );
}
